import { Timestamp } from 'firebase/firestore';
import { z } from 'zod';

/**
 * THREE-COLLECTION TEAM WORKFLOW TYPES
 * 
 * This file contains TypeScript interfaces and Zod validation schemas
 * for the new three-collection design:
 * 1. workflow_team_programs - Top level programs
 * 2. workflow_team_teams - Teams within programs  
 * 3. workflow_team_workflows - Individual workflows
 */

// ============================================================================
// CORE TYPE DEFINITIONS
// ============================================================================

/**
 * Workflow assignment status enumeration
 */
export type WorkflowAssignmentStatus = 
  | 'active'      // Currently being worked on
  | 'planning'    // In planning phase
  | 'completed'   // Work completed
  | 'on_hold'     // Temporarily paused
  | 'cancelled';  // No longer needed

/**
 * Workflow priority enumeration
 */
export type WorkflowPriority = 'high' | 'medium' | 'low';

// ============================================================================
// COLLECTION 1: WORKFLOW_TEAM_PROGRAMS
// ============================================================================

/**
 * Program document structure
 * Collection: workflow_team_programs
 * Document ID: User ID (program owner)
 */
export interface ProgramData {
  // Document identification
  id: string; // User ID of program owner
  program_name: string;
  owner_id: string; // User ID (same as document ID)
  owner_name: string;
  
  // Program metadata
  description?: string;
  
  // Team associations (array of team IDs)
  team_ids: string[];
  
  // Audit fields
  created_by: string; // user_id
  created_by_name: string;
  created_at: Timestamp;
  updated_at: Timestamp;
  last_modified_by: string; // user_id
  last_modified_by_name: string;
  
  // Version control
  version: number;
  
  // Optional metadata
  metadata?: {
    tags?: string[];
    priority?: WorkflowPriority;
  };
}

// ============================================================================
// COLLECTION 2: WORKFLOW_TEAM_TEAMS
// ============================================================================

/**
 * Notes version history entry
 */
export interface NotesVersionEntry {
  version: number;
  content: string;
  updated_at: Timestamp;
  updated_by: string;
  updated_by_name: string;
  change_summary?: string;
}

/**
 * Rich text notes section with metadata
 */
export interface TeamNotes {
  content: string; // Markdown formatted content
  last_updated: Timestamp;
  last_updated_by: string; // user_id
  last_updated_by_name: string;
  
  // Version history for notes (optional)
  version_history?: NotesVersionEntry[];
  
  // Content metadata
  word_count?: number;
  character_count?: number;
}

/**
 * Team document structure
 * Collection: workflow_team_teams
 * Document ID: User ID (team owner)
 */
export interface TeamData {
  // Document identification
  id: string; // User ID of team owner
  team_name: string;
  owner_id: string; // User ID (same as document ID)
  owner_name: string;
  
  // Program association
  program_id: string; // Reference to program document
  program_name: string;
  
  // Workflow associations (array of workflow IDs)
  workflow_ids: string[];
  
  // Manager notes with metadata
  notes: TeamNotes;
  
  // Audit fields
  created_by: string; // user_id
  created_by_name: string;
  created_at: Timestamp;
  updated_at: Timestamp;
  last_modified_by: string; // user_id
  last_modified_by_name: string;
  
  // Version control
  version: number;

  // Slack integration
  slack_channel_id?: string;

  // Optional metadata
  metadata?: {
    description?: string;
    tags?: string[];
    priority?: WorkflowPriority;
  };
}

// ============================================================================
// COLLECTION 3: WORKFLOW_TEAM_WORKFLOWS
// ============================================================================

/**
 * Individual workflow document structure
 * Collection: workflow_team_workflows
 * Document ID: Generated UID
 */
export interface WorkflowData {
  // Document identification
  id: string; // Generated UID
  workflow_name: string;
  
  // Team association
  team_id: string; // Reference to team document
  team_name: string;
  program_id: string; // Reference to program document (denormalized)
  program_name: string; // Denormalized for easier queries
  
  // Role assignments - exactly 4 roles as specified
  first_responder: string | null; // user_id
  production_support_1: string | null; // user_id
  production_support_2: string | null; // user_id
  strategy_pfm_support: string | null; // user_id
  
  // Assignment status and metadata
  status: WorkflowAssignmentStatus;
  priority: WorkflowPriority;
  
  // Audit fields
  created_by: string; // user_id
  created_by_name: string;
  created_at: Timestamp;
  updated_at: Timestamp;
  last_modified_by: string; // user_id
  last_modified_by_name: string;
  
  // Version control
  version: number;
  
  // Optional workflow-specific data
  estimated_hours?: number;
  actual_hours?: number;
  notes?: string;
  
  // Integration with existing workflow_configs
  workflow_config_ref?: string; // Reference to workflow_configs document
  
  // Optional metadata
  metadata?: {
    description?: string;
    tags?: string[];
  };
}

// ============================================================================
// AGGREGATION INTERFACES (for UI components)
// ============================================================================

/**
 * Program info for UI display (aggregated from multiple collections)
 */
export interface ProgramInfo {
  program_id: string;
  program_name: string;
  owner_id: string;
  owner_name: string;
  team_count: number;
  workflow_count: number;
  last_updated?: Date;
}

/**
 * Team info for UI display (aggregated from multiple collections)
 */
export interface TeamInfo {
  team_id: string;
  team_name: string;
  owner_id: string;
  owner_name: string;
  program_id: string;
  program_name: string;
  workflow_count: number;
  assignment_count: number;
  slack_channel_id?: string;
  last_updated?: Date;
}

/**
 * Workflow info for UI display
 */
export interface WorkflowInfo {
  workflow_id: string;
  workflow_name: string;
  team_id: string;
  team_name: string;
  program_id: string;
  program_name: string;
  first_responder: string | null;
  production_support_1: string | null;
  production_support_2: string | null;
  strategy_pfm_support: string | null;
  status: WorkflowAssignmentStatus;
  priority: WorkflowPriority;
  last_updated?: Date;
}

// ============================================================================
// CRUD OPERATION INTERFACES
// ============================================================================

/**
 * Data structure for creating new program
 */
export interface CreateProgramData {
  program_name: string;
  owner_id: string; // Will be used as document ID
  owner_name: string;
  description?: string;
  metadata?: {
    tags?: string[];
    priority?: WorkflowPriority;
  };
}

/**
 * Data structure for creating new team
 */
export interface CreateTeamData {
  team_name: string;
  owner_id: string; // Will be used as document ID
  owner_name: string;
  program_id: string;
  program_name: string;
  notes?: {
    content: string;
  };
  metadata?: {
    description?: string;
    tags?: string[];
    priority?: WorkflowPriority;
  };
}

/**
 * Data structure for creating new workflow
 */
export interface CreateWorkflowData {
  workflow_id: string; // Internal workflow ID to use as document ID
  workflow_name: string;
  team_id: string;
  team_name: string;
  program_id: string;
  program_name: string;
  first_responder?: string | null;
  production_support_1?: string | null;
  production_support_2?: string | null;
  strategy_pfm_support?: string | null;
  status?: WorkflowAssignmentStatus;
  priority?: WorkflowPriority;
  estimated_hours?: number;
  actual_hours?: number;
  notes?: string;
  workflow_config_ref?: string;
  metadata?: {
    description?: string;
    tags?: string[];
  };
}

/**
 * Data structure for updating program
 */
export interface UpdateProgramData {
  program_name?: string;
  description?: string;
  metadata?: {
    tags?: string[];
    priority?: WorkflowPriority;
  };
}

/**
 * Data structure for updating team
 */
export interface UpdateTeamData {
  team_name?: string;
  slack_channel_id?: string | null;
  notes?: Partial<TeamNotes>;
  metadata?: {
    description?: string;
    tags?: string[];
    priority?: WorkflowPriority;
  };
}

/**
 * Data structure for updating workflow
 */
export interface UpdateWorkflowData {
  workflow_name?: string;
  first_responder?: string | null;
  production_support_1?: string | null;
  production_support_2?: string | null;
  strategy_pfm_support?: string | null;
  status?: WorkflowAssignmentStatus;
  priority?: WorkflowPriority;
  estimated_hours?: number;
  actual_hours?: number;
  notes?: string;
  workflow_config_ref?: string;
  metadata?: {
    description?: string;
    tags?: string[];
  };
}

// ============================================================================
// ZOD VALIDATION SCHEMAS
// ============================================================================


/**
 * User ID validation (supports both Firebase UIDs and shorter user IDs)
 * Updated to accept user IDs of any length >= 1 character
 */
const UserIdSchema = z.string().min(1).max(128);

/**
 * Workflow assignment status validation
 */
const WorkflowAssignmentStatusSchema = z.enum([
  'active',
  'planning',
  'completed',
  'on_hold',
  'cancelled'
]);

/**
 * Workflow priority validation
 */
const WorkflowPrioritySchema = z.enum(['high', 'medium', 'low']);

/**
 * Create program validation
 */
export const CreateProgramDataSchema = z.object({
  program_name: z.string().min(1).max(100),
  owner_id: UserIdSchema,
  owner_name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  metadata: z.object({
    tags: z.array(z.string().max(50)).max(10).optional(),
    priority: WorkflowPrioritySchema.optional(),
  }).optional(),
});

/**
 * Create team validation
 */
export const CreateTeamDataSchema = z.object({
  team_name: z.string().min(1).max(100),
  owner_id: UserIdSchema,
  owner_name: z.string().min(1).max(100),
  program_id: UserIdSchema,
  program_name: z.string().min(1).max(100),
  notes: z.object({
    content: z.string().max(10000),
  }).optional(),
  metadata: z.object({
    description: z.string().max(500).optional(),
    tags: z.array(z.string().max(50)).max(10).optional(),
    priority: WorkflowPrioritySchema.optional(),
  }).optional(),
});

/**
 * Create workflow validation
 */
export const CreateWorkflowDataSchema = z.object({
  workflow_id: z.string().min(1), // Internal workflow ID to use as document ID
  workflow_name: z.string().min(1).max(200),
  team_id: UserIdSchema,
  team_name: z.string().min(1).max(100),
  program_id: UserIdSchema,
  program_name: z.string().min(1).max(100),
  first_responder: UserIdSchema.nullable().optional(),
  production_support_1: UserIdSchema.nullable().optional(),
  production_support_2: UserIdSchema.nullable().optional(),
  strategy_pfm_support: UserIdSchema.nullable().optional(),
  status: WorkflowAssignmentStatusSchema.optional(),
  priority: WorkflowPrioritySchema.optional(),
  estimated_hours: z.number().min(0).max(1000).optional(),
  actual_hours: z.number().min(0).max(1000).optional(),
  notes: z.string().max(1000).optional(),
  workflow_config_ref: z.string().optional(),
  metadata: z.object({
    description: z.string().max(500).optional(),
    tags: z.array(z.string().max(50)).max(10).optional(),
  }).optional(),
});